import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../api/axios'; // Import the centralized api instance

const AuthContext = createContext();

// JWT payload을 UTF-8 안전하게 디코딩 (atob만 쓰면 한글이 Latin-1로 깨짐)
export const decodeJwtPayload = (token) => {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
    return JSON.parse(new TextDecoder('utf-8').decode(bytes));
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    // 약관 동의 여부: null=조회 전(판단 보류) / true=동의함 / false=미동의 → /terms-agree 로 유도.
    // JWT 페이로드가 아니라 별도 조회로 둔다 (페이로드 변경 시 기존 토큰과 어긋나 전원 재로그인 필요).
    const [termsAgreed, setTermsAgreed] = useState(null);

    const refreshTermsStatus = useCallback(async () => {
        try {
            const res = await api.get('/auth/terms-status');
            const agreed = !!res.data?.data?.agreed;
            setTermsAgreed(agreed);
            return agreed;
        } catch {
            // 조회 실패 시 가드를 걸지 않는다 (서비스 접근을 막는 쪽이 더 위험).
            setTermsAgreed(null);
            return null;
        }
    }, []);

    // The logout function is simplified. Token removal will be handled by the response interceptor on 401,
    // but we provide an explicit logout method for user-initiated logout.
    const logout = useCallback(async () => {
        try {
            await api.post('/auth/logout');
        } catch (e) {
            // 실패해도 로컬 정리는 진행
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
        window.location.href = '/login';
    }, []);

    useEffect(() => {
        const initializeAuth = async () => {
            if (token) {
                try {
                    const decodedUser = decodeJwtPayload(token);
                    if (decodedUser.exp * 1000 < Date.now()) {
                        // access token 만료 → refresh 시도
                        try {
                            const res = await api.post('/auth/refresh');
                            const { accessToken, user: newUser } = res.data.data;
                            localStorage.setItem('token', accessToken);
                            localStorage.setItem('user', JSON.stringify(newUser));
                            setToken(accessToken);
                            setUser(newUser);
                        } catch {
                            logout();
                        }
                    } else {
                        setUser(decodedUser);
                    }
                    // 토큰이 유효한 동안 1회 조회 (로그인 직후·새로고침·refresh 모두 이 경로를 탄다)
                    await refreshTermsStatus();
                } catch (e) {
                    console.error("AuthContext - Error decoding token:", e);
                    logout();
                }
            } else {
                setTermsAgreed(null);
            }
            setLoading(false);
        };

        initializeAuth();
    }, [token, logout, refreshTermsStatus]);

    const login = async (username, password) => {
        try {
            // Use the centralized api instance. URL is relative to baseURL.
            const response = await api.post('/auth/login', { username, password });
            const { accessToken } = response.data.data;
            localStorage.setItem('token', accessToken);
            // The request interceptor in api/axios.js will handle adding the token to headers for subsequent requests.
            setToken(accessToken);
            // Decode user and set user state after successful login
            const decodedUser = decodeJwtPayload(accessToken);
            setUser(decodedUser);
            localStorage.setItem('user', JSON.stringify(decodedUser)); // Persist user info
        } catch (error) {
            console.error('Login failed:', error);
            if (error.response && error.response.data && error.response.data.errors) {
                const message = error.response.data.errors[0].msg;
                if (message === 'Invalid credentials') {
                    throw new Error('아이디 또는 비밀번호가 올바르지 않습니다.');
                }
                throw new Error(message);
            }
            throw new Error('로그인에 실패했습니다. 다시 시도해주세요.');
        }
    };

    // OAuth 콜백 / 셀프 가입 직후 받은 JWT 토큰으로 로그인 상태 세팅
    const loginWithToken = useCallback((accessToken) => {
        if (!accessToken) return;
        try {
            const decodedUser = decodeJwtPayload(accessToken);
            if (decodedUser.exp * 1000 < Date.now()) {
                throw new Error('Token already expired');
            }
            localStorage.setItem('token', accessToken);
            localStorage.setItem('user', JSON.stringify(decodedUser));
            setToken(accessToken);
            setUser(decodedUser);
        } catch (e) {
            console.error('loginWithToken failed:', e);
            throw new Error('인증 토큰이 올바르지 않습니다.');
        }
    }, []);

    const changePassword = async (currentPassword, password) => {
        try {
            // The auth header is automatically added by the interceptor.
            await api.put('/account/password', { currentPassword, password });
            // Success logic (e.g., notification) can be added here.
        } catch (error) {
            console.error('Password change failed:', error);
            if (error.response && error.response.data && error.response.data.errors) {
                throw new Error(error.response.data.errors[0].msg);
            }
            throw new Error('비밀번호 변경 중 오류가 발생했습니다.');
        }
    };

    // The response interceptor is now handled globally in api/axios.js, so the useEffect for it is removed.

    if (loading) {
        return <div>Loading authentication...</div>;
    }

    return (
        <AuthContext.Provider value={{ user, token, login, loginWithToken, logout, changePassword, termsAgreed, refreshTermsStatus }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
